/**
 * The single HTTP function every API call goes through (generated hooks and the auth client).
 *
 * - Same-origin requests with the session cookie (`/api`, `/_allauth` are proxied to Django).
 * - Sends Django's CSRF token on unsafe methods (read from the `csrftoken` cookie).
 * - JSON in/out; FormData is passed through for uploads.
 * - Non-2xx responses throw `ApiError` with our API error shape (ARCHITECT §6).
 */

import { ApiError, toApiError } from "./errors";

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * Server-side rendering (the Next.js storefront): API calls made on the server go to Django's internal address with
 * extra headers (the internal token and the visitor's IP). Browsers never use this; set once by the storefront.
 */
export interface ServerFetchConfig {
  baseUrl: string;
  /** Extra headers per call; gets the call's options (e.g. to forward the visitor only on uncached calls). */
  headers?: (init: RequestInit) => HeadersInit | Promise<HeadersInit>;
}

let server: ServerFetchConfig | null = null;

export function configureServerFetch(config: ServerFetchConfig | null): void {
  server = config;
}

let browserLanguage: string | null = null;

/**
 * The language the browser asks the API for (storefront, P6-13): sent as `Accept-Language` with every browser request,
 * so product and content texts come back translated. Server-side calls set it per request instead.
 */
export function setRequestLanguage(language: string | null): void {
  browserLanguage = language;
}

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  const type = response.headers.get("content-type") ?? "";
  if (type.includes("json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

/** Low-level request; resolves for 2xx, throws ApiError otherwise. */
export async function request<T>(url: string, init: RequestInit = {}): Promise<ApiResponse<T>> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (browserLanguage && typeof window !== "undefined" && !headers.has("Accept-Language")) {
    headers.set("Accept-Language", browserLanguage);
  }
  let body = init.body;
  if (body != null && !(body instanceof FormData) && typeof body !== "string") {
    body = JSON.stringify(body);
  }
  if (typeof body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (UNSAFE_METHODS.has(method)) {
    const token = readCookie("csrftoken");
    if (token) headers.set("X-CSRFToken", token);
  }

  let target = url;
  if (server && typeof window === "undefined" && url.startsWith("/")) {
    target = `${server.baseUrl.replace(/\/$/, "")}${url}`;
    if (server.headers)
      new Headers(await server.headers(init)).forEach((value, key) => headers.set(key, value));
  }

  let response: Response;
  try {
    response = await fetch(target, { ...init, method, headers, body, credentials: "same-origin" });
  } catch (cause) {
    throw new ApiError({
      status: 0,
      type: "network_error",
      message: "Can't reach the server. Check your connection.",
      cause,
    });
  }
  const data = await parseBody(response);
  if (!response.ok) {
    throw toApiError(response.status, data, response.headers.get("X-Request-ID"));
  }
  return { data: data as T, status: response.status, headers: response.headers };
}

/**
 * Orval mutator: generated endpoint functions call this and expect `{ data, status, headers }`.
 * (The generic is the full response type orval computes.)
 */
export const apiFetch = async <T>(url: string, init?: RequestInit): Promise<T> => {
  return (await request<unknown>(url, init)) as T;
};
